import React from "react";
import { PageHero } from "@/components/SectionHeader";
import { COMPANY, ABOUT_IMAGE } from "@/lib/siteData";
import { Award, Compass, Target } from "lucide-react";

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        testid="about-hero"
        eyebrow="About Us"
        title="Built on 50 years of chemical heritage."
        description="Our parent company has been engaged in chemical manufacturing for the past 50 years — and we have now ventured into plasticisers, catering to multiple industries."
      />

      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7">
          <h2 className="font-display text-3xl sm:text-4xl font-bold uppercase tracking-tight text-slate-900">
            Our Story
          </h2>
          <div className="mt-6 space-y-5 text-slate-700 leading-relaxed">
            <p>
              Our parent company has been engaged in the manufacturing of
              chemicals for the past 50 years. Backed by that legacy, we
              combine seasoned process expertise with fresh, purpose-built
              infrastructure — and have now ventured into plasticisers,
              bringing world-class manufacturing to Eastern India for the
              first time.
            </p>
            <p>
              Our facility is engineered around four high-demand plasticisers —
              DOTP, DOP, DINP and TOTM — each engineered to consistent,
              compound-ready specifications. Strategic port access in Eastern
              India enables competitive raw material sourcing, which translates
              into reliable pricing for our customers across the PVC value
              chain.
            </p>
            <p>
              Today we operate {COMPANY.capacityNow} tonnes/year of
              plasticiser capacity, with a clear roadmap to{" "}
              {COMPANY.capacityFuture} tonnes/year within the next{" "}
              {COMPANY.expansionYears} years.
            </p>
            <p>
              DOTP currently accounts for over 80% of our production,
              driven by surging demand and the industry's rapid shift
              toward non-phthalate plasticisers. We are proud pioneers of
              this shift and currently lead the Eastern India market.
            </p>
          </div>
        </div>

        <div className="lg:col-span-5 space-y-5">
          <div
            className="rounded-2xl overflow-hidden border border-slate-100 shadow-sm bg-slate-50"
            data-testid="about-fleet-image"
          >
            <img
              src={ABOUT_IMAGE}
              alt="RPG Rasayan Udyog fleet, drums and IBC totes ready for pan-India dispatch"
              className="w-full h-auto object-contain"
            />
          </div>
          <ValueBlock
            testid="about-mission"
            icon={<Target size={20} />}
            title="Mission"
            body="To be Eastern India's most reliable plasticiser partner — delivering consistent quality, predictable supply, and structural cost advantage to the PVC value chain."
          />
          <ValueBlock
            testid="about-vision"
            icon={<Compass size={20} />}
            title="Vision"
            body="To grow into a national-scale specialty plasticiser manufacturer, serving wires & cables, automotive, medical, packaging, and construction industries with the same rigor as global majors."
          />
          <ValueBlock
            testid="about-legacy"
            icon={<Award size={20} />}
            title="Parent Legacy"
            body={`Five decades of chemical-industry experience powers every decision — from process engineering to procurement to customer service.`}
          />
        </div>
      </section>

      <section className="bg-slate-50 border-y border-slate-100 py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-8">
          <Pillar
            testid="pillar-quality"
            num="01"
            title="Consistent Quality"
            body="Tight in-process controls and lab-tested batch release for every shipment."
          />
          <Pillar
            testid="pillar-reliability"
            num="02"
            title="Supply Reliability"
            body="Strategic feedstock sourcing through Eastern India ports keeps lead times short."
          />
          <Pillar
            testid="pillar-partnership"
            num="03"
            title="Customer Partnership"
            body="Technical support from compounders who understand application chemistry."
          />
        </div>
      </section>
    </div>
  );
}

const ValueBlock = ({ icon, title, body, testid }) => (
  <div
    className="bg-white rounded-2xl border border-slate-100 p-6"
    data-testid={testid}
  >
    <div className="flex items-center gap-3 mb-3">
      <span className="w-9 h-9 rounded-lg gradient-accent-soft flex items-center justify-center text-indigo-600">
        {icon}
      </span>
      <h3 className="font-display text-xl uppercase font-bold text-slate-900">
        {title}
      </h3>
    </div>
    <p className="text-slate-600 leading-relaxed text-sm">{body}</p>
  </div>
);

const Pillar = ({ num, title, body, testid }) => (
  <div data-testid={testid}>
    <p className="font-display text-5xl gradient-text font-bold">{num}</p>
    <h3 className="font-display text-2xl uppercase font-bold text-slate-900 mt-2">
      {title}
    </h3>
    <p className="text-slate-600 mt-3 leading-relaxed">{body}</p>
  </div>
);
